import type { ChatMessage, ChatPart } from '@core/tellent-assistant-types';

type TextPart = Extract<ChatPart, { kind: 'text' }>;
type ToolCallPart = Extract<ChatPart, { kind: 'tool-call' }>;
type ToolResultPart = Extract<ChatPart, { kind: 'tool-result' }>;

const SUB_AGENT_TOOL_NAME_PREFIX = 'agent-';

/**
 * Mastra's ToolSearchProcessor plumbing surfaces these as tool-call events, but they're
 * the search/load mechanism itself — not user-facing work. Hide them from the timeline so
 * the thinking section shows only meaningful steps.
 */
const HIDDEN_META_TOOL_NAMES = new Set(['search_tools', 'load_tool']);

/**
 * Matches a component placeholder token: `{{component:<id>}}`.
 * The id is produced by the backend `defineRenderTool` factory as `<toolId>_<short>`
 * (e.g. `presentTasks_a7f3k29q`). Kept lenient enough to accept any non-empty
 * alphanumeric/underscore id without trapping common markdown characters.
 */
const COMPONENT_TOKEN_REGEX = /\{\{component:([a-zA-Z0-9_]+)\}\}/g;

/** One row in the thinking-section timeline for an assistant message. */
export interface TimelineToolEvent {
  kind: 'tool';
  toolCallId: string;
  name: string;
  status: ToolCallPart['status'];
  /** Provenance — non-empty when this row came from a flattened `agent-*` sub-agent. */
  subAgentToolName?: string;
}

/** Reasoning row in the thinking-section timeline (rendered via the text-part component). */
export interface TimelineReasoningEvent {
  kind: 'reasoning';
  messageId: string;
  content: string;
  done: boolean;
  /** Provenance — non-empty when this row came from a flattened `agent-*` sub-agent. */
  subAgentToolName?: string;
}

/** Discriminated union of all thinking-section row kinds. */
export type TimelineEvent = TimelineToolEvent | TimelineReasoningEvent;

/**
 * One entry in the assistant message body.
 *
 * Layout is driven by `{{component:<id>}}` tokens inside the assistant's prose — NOT by
 * the streaming order of tool calls vs. text. A text part is split into segments around
 * its tokens; each matched token expands into a `render-tool` entry mounting the
 * presenter for the corresponding tool call. Render tool calls without a referencing
 * token are invisible by design (component-addressable-from-prose protocol). Render
 * tool calls that errored before producing a componentId are surfaced as `render-error`
 * pills at the end of the body so the user is not left guessing.
 */
export type BodyEntry =
  | { kind: 'text'; key: string; content: string; done: boolean }
  | { kind: 'render-tool'; key: string; part: ToolCallPart }
  | { kind: 'render-error'; key: string; toolName: string };

/** Split of an assistant `ChatMessage` into timeline rows + body entries. */
export interface AssistantMessageView {
  timeline: TimelineEvent[];
  body: BodyEntry[];
}

/**
 * Split an assistant message into a thinking timeline + a renderable body.
 *
 * `renderToolNames` is the set of tool ids that have a registered FE presenter — those
 * tool-call parts are eligible to render in the body, but only if their `componentId`
 * (returned in the matching `tool-result`) is referenced via a `{{component:<id>}}`
 * token in one of the message's text parts. Every other tool-call becomes a timeline
 * row. Sub-agent (`agent-*`) tool calls are flattened: their inner reasoning and tool
 * steps inline into the parent timeline, tagged with `subAgentToolName`.
 */
export interface SplitOptions {
  /** When true, sub-agent (`agent-*`) inner steps surface inside the parent thinking timeline. */
  subagentAsThinking?: boolean;
}

export const splitAssistantMessage = (
  msg: ChatMessage,
  renderToolNames: ReadonlySet<string>,
  options: SplitOptions = {},
): AssistantMessageView => {
  if (msg.role !== 'assistant') return { timeline: [], body: [] };

  const body = buildBody(msg.parts, renderToolNames);
  const timeline = collectTimeline(msg.parts, renderToolNames, {
    flattenText: false,
    subagentAsThinking: options.subagentAsThinking ?? false,
  });

  return { timeline, body };
};

function buildBody(parts: readonly ChatPart[], renderToolNames: ReadonlySet<string>): BodyEntry[] {
  const renderCalls = parts.filter(
    (p): p is ToolCallPart => p.kind === 'tool-call' && renderToolNames.has(p.name),
  );
  const callByComponentId = buildComponentRegistry(parts, renderCalls);
  const used = new Set<string>();
  const body: BodyEntry[] = [];
  let segCounter = 0;
  const nextKey = (prefix: string) => `${prefix}-${segCounter++}`;

  for (const textPart of parts.filter((p): p is TextPart => p.kind === 'text')) {
    expandTextPart(textPart, callByComponentId, used, body, nextKey);
  }

  for (const call of renderCalls) {
    if (call.status !== 'error') continue;
    body.push({
      kind: 'render-error',
      key: `rerr-${call.toolCallId}`,
      toolName: call.name,
    });
  }

  return body;
}

function buildComponentRegistry(
  parts: readonly ChatPart[],
  renderCalls: readonly ToolCallPart[],
): Map<string, ToolCallPart> {
  const resultByCallId = new Map<string, unknown>(
    parts
      .filter((p): p is ToolResultPart => p.kind === 'tool-result')
      .map(p => [p.toolCallId, p.payload]),
  );
  const registry = new Map<string, ToolCallPart>();
  for (const call of renderCalls) {
    const componentId = extractComponentId(resultByCallId.get(call.toolCallId));
    if (componentId !== undefined && !componentId.startsWith('ERROR:')) {
      registry.set(componentId, call);
    }
  }
  return registry;
}

function expandTextPart(
  textPart: TextPart,
  callByComponentId: ReadonlyMap<string, ToolCallPart>,
  used: Set<string>,
  body: BodyEntry[],
  nextKey: (prefix: string) => string,
): void {
  const safeContent = textPart.done
    ? textPart.content
    : chompPartialTrailingToken(textPart.content);

  let lastEnd = 0;
  COMPONENT_TOKEN_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = COMPONENT_TOKEN_REGEX.exec(safeContent)) !== null) {
    const before = safeContent.slice(lastEnd, match.index);
    if (before.length > 0) {
      body.push({
        kind: 'text',
        key: nextKey(`${textPart.messageId}-t`),
        content: before,
        done: true,
      });
    }
    const componentId = match[1]!;
    const call = callByComponentId.get(componentId);
    if (call && !used.has(componentId)) {
      used.add(componentId);
      body.push({
        kind: 'render-tool',
        key: `${textPart.messageId}-rt-${componentId}`,
        part: call,
      });
    }
    lastEnd = match.index + match[0].length;
  }

  const tail = safeContent.slice(lastEnd);
  if (tail.length > 0) {
    body.push({
      kind: 'text',
      key: nextKey(`${textPart.messageId}-t`),
      content: tail,
      done: textPart.done,
    });
  }
}

function extractComponentId(payload: unknown): string | undefined {
  if (typeof payload !== 'object' || payload === null) return undefined;
  const value = (<{ componentId?: unknown }>payload).componentId;
  return typeof value === 'string' ? value : undefined;
}

/**
 * While a text part is still streaming, hide any trailing `{{...` that hasn't closed.
 * Avoids briefly flashing raw token syntax before it resolves to a component.
 */
function chompPartialTrailingToken(text: string): string {
  const lastOpen = text.lastIndexOf('{{');
  if (lastOpen === -1) return text;
  if (text.indexOf('}}', lastOpen) !== -1) return text;
  return text.slice(0, lastOpen);
}

interface CollectContext {
  /** Provenance tag — set when walking inside a flattened `agent-*` sub-agent message. */
  subAgentToolName?: string;
  /** When true, text parts are converted to reasoning events (sub-agent only). */
  flattenText: boolean;
  /** When true, sub-agent inner steps inline into the parent timeline. */
  subagentAsThinking: boolean;
}

function collectTimeline(
  parts: readonly ChatPart[],
  renderToolNames: ReadonlySet<string>,
  context: CollectContext,
): TimelineEvent[] {
  const events: TimelineEvent[] = [];
  for (const part of parts) {
    if (part.kind === 'reasoning') {
      events.push({
        kind: 'reasoning',
        messageId: part.messageId,
        content: part.content,
        done: part.done,
        ...(context.subAgentToolName !== undefined
          ? { subAgentToolName: context.subAgentToolName }
          : {}),
      });
      continue;
    }
    if (part.kind === 'text' && context.flattenText) {
      events.push({
        kind: 'reasoning',
        messageId: part.messageId,
        content: part.content,
        done: part.done,
        ...(context.subAgentToolName !== undefined
          ? { subAgentToolName: context.subAgentToolName }
          : {}),
      });
      continue;
    }
    if (part.kind !== 'tool-call') continue;
    if (renderToolNames.has(part.name)) continue;
    if (HIDDEN_META_TOOL_NAMES.has(part.name)) continue;

    if (part.name.startsWith(SUB_AGENT_TOOL_NAME_PREFIX)) {
      if (!context.subagentAsThinking) continue;
      for (const subMsg of part.subAgentMessages ?? []) {
        events.push(
          ...collectTimeline(subMsg.parts, renderToolNames, {
            subAgentToolName: part.name,
            flattenText: true,
            subagentAsThinking: true,
          }),
        );
      }
      continue;
    }

    events.push({
      kind: 'tool',
      toolCallId: part.toolCallId,
      name: part.name,
      status: part.status,
      ...(context.subAgentToolName !== undefined
        ? { subAgentToolName: context.subAgentToolName }
        : {}),
    });
  }
  return events;
}
